/**
 * EventDateTimeRow
 *
 * Start/End card layout for date/time display.
 * - All-day: cards show date only
 * - Non-all-day: cards show date + bold time
 */

import { StyleSheet, Text, TouchableOpacity, View, Switch } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';

type EventDateTimeRowProps = {
  allDay: boolean;
  startDate: string;
  startTime: string | null;
  endDate: string;
  endTime: string | null;
  onToggleAllDay: (value: boolean) => void;
  onPressStartDate: () => void;
  onPressEndDate: () => void;
  palette: typeof Colors.light;
};

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  const currentYear = new Date().getFullYear();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(year !== currentYear && { year: 'numeric' }),
  };
  return parsed.toLocaleDateString(undefined, options);
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EventDateTimeRow({
  allDay,
  startDate,
  startTime,
  endDate,
  endTime,
  onToggleAllDay,
  onPressStartDate,
  onPressEndDate,
  palette,
}: EventDateTimeRowProps) {
  return (
    <View style={styles.container}>
      {/* Icon */}
      <View style={styles.iconContainer}>
        <Ionicons name="time-outline" size={18} color={palette.textMuted} />
      </View>

      {/* Content */}
      <View style={styles.content}>
        {/* All Day Toggle Row */}
        <View style={styles.allDayRow}>
          <Text style={[styles.allDayLabel, { color: palette.text, fontFamily: getBodyFont() }]}>
            All-day
          </Text>
          <Switch
            value={allDay}
            onValueChange={onToggleAllDay}
            trackColor={{ true: palette.tint, false: '#d4d4d8' }}
            thumbColor="#ffffff"
          />
        </View>

        {/* Start / End Cards */}
        <View style={styles.cardsRow}>
          <TouchableOpacity
            onPress={onPressStartDate}
            style={[styles.card, { backgroundColor: palette.cardBackground }]}
          >
            <Text style={[styles.cardLabel, { color: palette.textMuted }]}>
              START
            </Text>
            <Text style={[styles.cardDate, { color: palette.text, fontFamily: getBodyFont() }]}>
              {formatDate(startDate)}
            </Text>
            {!allDay && startTime && (
              <Text style={[styles.cardTime, { color: palette.text, fontFamily: getBodyFont() }]}>
                {formatTime(startTime)}
              </Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            onPress={onPressEndDate}
            style={[styles.card, { backgroundColor: palette.cardBackground }]}
          >
            <Text style={[styles.cardLabel, { color: palette.textMuted }]}>
              END
            </Text>
            <Text style={[styles.cardDate, { color: palette.text, fontFamily: getBodyFont() }]}>
              {formatDate(endDate)}
            </Text>
            {!allDay && endTime && (
              <Text style={[styles.cardTime, { color: palette.text, fontFamily: getBodyFont() }]}>
                {formatTime(endTime)}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    paddingTop: Spacing.xs,
  },
  content: {
    flex: 1,
    gap: Spacing.md,
  },
  allDayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  allDayLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  cardsRow: {
    flexDirection: 'row',
    gap: Spacing.md,
  },
  card: {
    flex: 1,
    borderRadius: 10,
    paddingHorizontal: Spacing.md,
    paddingVertical: 10,
  },
  cardLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  cardDate: {
    fontSize: 14,
    fontWeight: '500',
  },
  cardTime: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: Spacing.xs,
  },
});
